import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type ApiError, type DataQualityResult } from '@whinerp/client';
import { formatCount, formatPercent } from '@whinerp/contracts/format';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * Data quality at a glance (G-275): per object, how complete, how duplicated,
 * how stale - with each check counted by name so it is a worklist, not a grade.
 */

const LIST: Record<string, string> = { contact: '/contacts', account: '/accounts', lead: '/leads', deal: '/deals' };

export function DataQualityPanel(): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const { can } = useSession();
  const enabled = can('setup.view');
  const quality = useQuery<DataQualityResult, ApiError>({ queryKey: ['data-quality'], queryFn: () => api.dataQuality({}), enabled });
  if (!enabled || !quality.data) return null;
  return (
    <Panel data-region="data-quality">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <SectionLabel>{t('dataQuality.title')}</SectionLabel>
          <p className="text-meta text-ink-meta">{t('dataQuality.body')}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {quality.data.objects.map((row) => (
            <div key={row.object} data-quality-object={row.object} className="flex flex-col gap-1 rounded-control border border-rule p-3">
              <div className="flex items-baseline gap-2">
                <Link to={LIST[row.object] ?? '/'} className="text-body font-medium text-ink hover:text-bronze-text">{t(`dataQuality.object.${row.object}`)}</Link>
                <span className="text-meta text-ink-meta">{formatCount(row.total, format)}</span>
                <span className="ms-auto text-dense text-ink" data-quality-completeness>
                  {row.completeness === null ? '-' : t('dataQuality.complete', { values: { percent: formatPercent(row.completeness, format) } })}
                </span>
              </div>
              <ul className="flex flex-col text-dense">
                {row.checks.filter((check) => check.count > 0).map((check) => (
                  <li key={check.key} data-quality-check={check.key} className="flex">
                    <span className="text-ink-meta">{t(`dataQuality.check.${check.key}` as 'dataQuality.check.noEmail')}</span>
                    <span className="ms-auto text-ink">{formatCount(check.count, format)}</span>
                  </li>
                ))}
                {row.object !== 'deal' ? (
                  <li data-quality-duplicates className="flex">
                    <span className="text-ink-meta">{t('dataQuality.duplicates', { values: { groups: formatCount(row.duplicateGroups, format) } })}</span>
                    <span className="ms-auto text-ink">{formatCount(row.duplicateRecords, format)}</span>
                  </li>
                ) : null}
                <li data-quality-stale className="flex">
                  <span className="text-ink-meta">{t('dataQuality.stale', { values: { days: String(row.staleDays) } })}</span>
                  <span className="ms-auto text-ink">{formatCount(row.stale, format)}</span>
                </li>
              </ul>
              {row.topDuplicates.length > 0 ? (
                <p className="text-meta text-ink-meta" data-quality-top-duplicates>
                  {t('dataQuality.topDuplicates', { values: { list: row.topDuplicates.map((dup) => `${dup.value} (${String(dup.count)})`).join(', ') } })}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
}
